import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Inbox, KeyRound, Plug } from "lucide-react";
import { toast } from "react-toastify";
import { integrationAPI } from "../../services/api";
import Button from "../UI/Button";
import SectionCard from "../UI/SectionCard";
import { Modal } from "../UI/Modal";
import { FormField, Input } from "../UI/FormField";
import LoadingSpinner from "../UI/LoadingSpinner";
import ConfirmationDialog from "../modals/ConfirmationDialog";
import { ToggleSwitch } from "../shipments/ToggleSwitch";
import type { OliverConnection } from "../../types/containerOrderIntake";

const STATUS_BADGE: Record<OliverConnection["status"], string> = {
  pending: "bg-gray-100 text-gray-700",
  connected: "bg-green-100 text-green-800",
  error: "bg-red-100 text-red-700",
};

const fmtDateTime = (d?: string | null) =>
  d ? new Date(d).toLocaleString("et-EE") : null;

const errorText = (e: any, fallback: string) =>
  e?.response?.data?.message || fallback;

/**
 * Oliver — the order-reading service whose queue becomes container order
 * intakes. An admin pastes the token Oliver issued, and the server polls
 * the queue every minute from then on.
 */
const OliverConnectionCard: React.FC = () => {
  const { t } = useTranslation("containerOrderIntake");
  const navigate = useNavigate();

  const [connection, setConnection] = useState<OliverConnection | null>(null);
  const [canStoreToken, setCanStoreToken] = useState(true);
  const [loading, setLoading] = useState(true);

  const [tokenOpen, setTokenOpen] = useState(false);
  const [token, setToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);

  const load = async () => {
    try {
      const res = await integrationAPI.getOliver();
      setConnection(res.data.data.connection);
      setCanStoreToken(res.data.data.canStoreToken);
    } catch (e: any) {
      toast.error(errorText(e, t("connection.loadFailed")));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveToken = async () => {
    setSaving(true);
    try {
      const res = await integrationAPI.saveOliver({ token: token.trim(), enabled: true });
      setConnection(res.data.data.connection);
      toast.success(res.data.message);
      setTokenOpen(false);
      setToken("");
    } catch (e: any) {
      toast.error(errorText(e, t("connection.saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await integrationAPI.testOliver();
      toast.success(res.data.message);
    } catch (e: any) {
      toast.error(errorText(e, t("connection.testFailed")));
    } finally {
      setTesting(false);
      load();
    }
  };

  const handleToggle = async () => {
    if (!connection) return;
    try {
      const res = await integrationAPI.setOliverEnabled(!connection.enabled);
      setConnection(res.data.data.connection);
    } catch (e: any) {
      toast.error(errorText(e, t("connection.saveFailed")));
    }
  };

  const handleDisconnect = async () => {
    try {
      await integrationAPI.disconnectOliver();
      setConnection(null);
    } catch (e: any) {
      toast.error(errorText(e, t("connection.saveFailed")));
    }
  };

  const title = t("connection.title");

  if (loading) {
    return (
      <SectionCard icon={Inbox} title={title}>
        <div className="flex justify-center py-8">
          <LoadingSpinner size="md" />
        </div>
      </SectionCard>
    );
  }

  const label = (key: string) => (
    <div className="text-[11px] font-semibold text-[#8A9590] uppercase tracking-wide">
      {t(key)}
    </div>
  );

  return (
    <>
      <SectionCard
        icon={Inbox}
        title={title}
        actions={
          <span
            className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${
              connection ? STATUS_BADGE[connection.status] : "bg-gray-100 text-gray-600"
            }`}
          >
            {connection ? t(`connection.status.${connection.status}`) : t("connection.notConnected")}
          </span>
        }
      >
        <p className="text-[13px] text-[#8A9590] mb-3">{t("connection.subtitle")}</p>

        {!canStoreToken && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-[13px] rounded-md px-3 py-2 mb-3">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>{t("connection.noEncryptionKey")}</div>
          </div>
        )}

        {!connection ? (
          <Button
            variant="primary"
            size="sm"
            icon={<Plug className="h-4 w-4" />}
            onClick={() => setTokenOpen(true)}
            disabled={!canStoreToken}
          >
            {t("connection.connect")}
          </Button>
        ) : (
          <div className="space-y-3">
            {connection.status === "error" && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-800 text-[13px] rounded-md px-3 py-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  {connection.authFailed ? t("connection.authFailedHint") : t("connection.errorHint")}
                  {connection.lastError && (
                    <div className="mt-1 text-[11px] opacity-75 break-all">{connection.lastError}</div>
                  )}
                </div>
              </div>
            )}

            {/* No "orders received" tally: the counter lives on this connection
                document, so replacing the token or reconnecting restarts it at
                zero while the orders themselves are all still in Order Intake —
                a number that disagrees with the page it describes. The queue is
                the count. */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-[13px]">
              <div>
                {label("connection.token")}
                <div className="font-mono text-[12px]">••••{connection.tokenHint}</div>
              </div>
              <div>
                {label("connection.lastPolled")}
                <div>{fmtDateTime(connection.lastPolledAt) ?? t("connection.never")}</div>
              </div>
              <div>
                {label("connection.lastSuccess")}
                <div>{fmtDateTime(connection.lastSuccessAt) ?? t("connection.never")}</div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-1">
              <ToggleSwitch
                checked={connection.enabled}
                onChange={handleToggle}
                label={connection.enabled ? t("connection.pollingOn") : t("connection.pollingOff")}
              />
              <Button variant="outline" size="sm" onClick={handleTest} isLoading={testing}>
                {t("connection.test")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                icon={<KeyRound className="h-4 w-4" />}
                onClick={() => setTokenOpen(true)}
                disabled={!canStoreToken}
              >
                {t("connection.replaceToken")}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigate("/order-intake")}>
                {t("connection.openQueue")}
              </Button>
              <Button variant="danger" size="sm" onClick={() => setDisconnectOpen(true)}>
                {t("connection.disconnect")}
              </Button>
            </div>
          </div>
        )}
      </SectionCard>

      <Modal isOpen={tokenOpen} onClose={() => setTokenOpen(false)} title={title} size="md">
        <div className="flex-1 overflow-y-auto px-4 py-4 sm:p-6 space-y-4">
          <p className="text-[13px] text-[#4A5550]">{t("connection.tokenHelp")}</p>
          <FormField label={t("connection.token")} required hint={t("connection.tokenFieldHint")}>
            <Input
              type="password"
              autoComplete="off"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="tm_…"
            />
          </FormField>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setTokenOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSaveToken}
              isLoading={saving}
              disabled={token.trim().length < 8}
            >
              {t("connection.saveToken")}
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmationDialog
        isOpen={disconnectOpen}
        onClose={() => setDisconnectOpen(false)}
        onConfirm={() => {
          setDisconnectOpen(false);
          handleDisconnect();
        }}
        title={t("connection.disconnect")}
        message={t("connection.disconnectConfirm")}
        confirmText={t("connection.disconnect")}
        type="danger"
      />
    </>
  );
};

export default OliverConnectionCard;
